import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { guestLogin } from '../services/authService';
import { useRegistration } from '../store/RegistrationContext';
import { registrationActionTypes } from '../store/registrationReducer';

export default function GuestLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { dispatch } = useRegistration();

  const nextPath = location.state?.from || '/students';

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const canSubmit = useMemo(() => {
    return (
      formData.firstName.trim() &&
      formData.lastName.trim() &&
      formData.email.trim() &&
      formData.phone.trim()
    );
  }, [formData]);

  const validate = () => {
    const next = {};
    if (!formData.firstName.trim()) next.firstName = 'First name is required';
    if (!formData.lastName.trim()) next.lastName = 'Last name is required';

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) next.email = 'Email is required';
    else if (!emailRegex.test(formData.email)) next.email = 'Please enter a valid email address';

    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (!phoneDigits) next.phone = 'Phone number is required';
    else if (phoneDigits.length !== 10) next.phone = 'Phone number must be exactly 10 digits';
    else if (!/^[2-9]\d{9}$/.test(phoneDigits)) next.phone = 'Please enter a valid US phone number';

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onChange = (name) => (e) => {
    const value = e.target.value;
    setFormData((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: '' }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!validate()) return;

    setLoading(true);
    const result = await guestLogin({
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone
    });
    setLoading(false);

    if (!result.success) {
      setSubmitError(result.error);
      return;
    }

    dispatch({
      type: registrationActionTypes.LOGIN_SUCCESS,
      payload: {
        email: formData.email,
        entity: process.env.REACT_APP_ENTITY || 'RMS',
        token: result.token,
        userData: {
          ...result.user,
          token: result.token
        }
      }
    });

    navigate(nextPath);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fadeIn overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-gray-100">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(0,0,0,.05) 35px, rgba(0,0,0,.05) 70px)'
          }}
        />
      </div>

      <div className="relative bg-white rounded-2xl shadow-[0_20px_70px_rgba(0,0,0,0.08)] max-w-md w-full p-10 animate-slideInUp border border-gray-100">
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-black to-transparent" />

        <div className="text-center mb-10 relative">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-black rounded-2xl shadow-xl mb-5 transform hover:scale-105 transition-all duration-300">
            <i className="fa-solid fa-user text-white text-2xl" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2 tracking-tight">Continue as Guest</h2>
          <p className="text-gray-500 text-sm">Enter your details to continue</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-6">
          <div className="relative">
            <input
              name="firstName"
              value={formData.firstName}
              onChange={onChange('firstName')}
              className={`w-full px-4 py-4 bg-white border-2 rounded-xl focus:outline-none transition-all duration-200 text-base text-gray-900 placeholder-gray-400 ${
                errors.firstName
                  ? 'border-red-500 focus:border-red-500'
                  : 'border-gray-200 focus:border-black focus:ring-2 focus:ring-gray-100'
              }`}
              placeholder="First Name"
              disabled={loading}
            />
            {errors.firstName ? <p className="text-red-500 text-xs mt-1">{errors.firstName}</p> : null}
          </div>

          <div className="relative">
            <input
              name="lastName"
              value={formData.lastName}
              onChange={onChange('lastName')}
              className={`w-full px-4 py-4 bg-white border-2 rounded-xl focus:outline-none transition-all duration-200 text-base text-gray-900 placeholder-gray-400 ${
                errors.lastName
                  ? 'border-red-500 focus:border-red-500'
                  : 'border-gray-200 focus:border-black focus:ring-2 focus:ring-gray-100'
              }`}
              placeholder="Last Name"
              disabled={loading}
            />
            {errors.lastName ? <p className="text-red-500 text-xs mt-1">{errors.lastName}</p> : null}
          </div>

          <div className="relative">
            <input
              name="email"
              value={formData.email}
              onChange={onChange('email')}
              className={`w-full px-4 py-4 bg-white border-2 rounded-xl focus:outline-none transition-all duration-200 text-base text-gray-900 placeholder-gray-400 ${
                errors.email
                  ? 'border-red-500 focus:border-red-500'
                  : 'border-gray-200 focus:border-black focus:ring-2 focus:ring-gray-100'
              }`}
              placeholder="Email Address"
              disabled={loading}
            />
            {errors.email ? <p className="text-red-500 text-xs mt-1">{errors.email}</p> : null}
          </div>

          <div className="relative">
            <input
              name="phone"
              value={formData.phone}
              onChange={onChange('phone')}
              className={`w-full px-4 py-4 bg-white border-2 rounded-xl focus:outline-none transition-all duration-200 text-base text-gray-900 placeholder-gray-400 ${
                errors.phone
                  ? 'border-red-500 focus:border-red-500'
                  : 'border-gray-200 focus:border-black focus:ring-2 focus:ring-gray-100'
              }`}
              placeholder="Phone Number"
              disabled={loading}
            />
            {errors.phone ? <p className="text-red-500 text-xs mt-1">{errors.phone}</p> : null}
          </div>

          {submitError ? (
            <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-4 animate-shake">
              <div className="flex items-center gap-3">
                <i className="fa-solid fa-triangle-exclamation text-red-500" />
                <p className="text-red-700 text-sm font-medium">{submitError}</p>
              </div>
            </div>
          ) : null}

          <div className="flex gap-3 pt-6">
            <button
              type="button"
              onClick={() => navigate('/login', { state: { from: nextPath } })}
              disabled={loading}
              className="flex-1 bg-white border-2 border-gray-200 text-gray-700 font-semibold py-4 px-4 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Back
            </button>

            <button
              type="submit"
              disabled={!canSubmit || loading}
              className="flex-1 bg-black text-white font-bold py-4 px-4 rounded-xl hover:bg-gray-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Continuing...
                </span>
              ) : (
                'Continue as Guest'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
